import * as React from 'react';
import {
    Button,
    ButtonGroup,
    ButtonToolbar,
    FormGroup,
    ControlLabel,
    FormControl,
    Row,
    Col
} from 'react-bootstrap';
import DateTime from 'react-datetime';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Async from 'react-select/lib/Async';
import notify from '../../../../shared/feedback/notifications';
import * as actions from '../../OrdersActions';
import { handleError } from '../../../../shared/ErrorCatching';
import { Translate } from '../../../../shared/LanguageService';
import FormInputChangeHandler from '../../../../shared/form/FormInputChangeHandler';
import FormSelect from '../../../../components/common/FormSelect';
import Pagination from '../../../../components/common/Pagination';
import { assign } from '../../../../shared/Assign';
import { debounce } from '../../../../shared/Utilities';
import List from './AdvanceSearchList';
import { IOrder } from './Entities';
import Loader from 'react-loader';
import moment from 'moment';

interface IProps {
    orders: IOrder[];
    user: any;
    availablePhotoStatuses: ISelect[];
    availablePlanStatuses: ISelect[];
    availableInvoiceStatuses: ISelect[];
    availableBrokerChains: ISelect[];
    availableRegions: ISelect[];
    availablePhotographers: ISelect[];
    availableProducts: ISelect[];
    availableProductTypes: ISelect[];
    availableSellers: ISelect[];

    availableInvoiceReceiverTypes: ISelect[];
    availableContentProviders: ISelect[];
    availableBookingTypes: ISelect[];
    availableOrderFilters: ISelect[];
    availableEditStyles: ISelect[];

    filter: IFilter;

    totalNumberOfOrders: number;
    numberOfPages: number;

    router: any;
    actions: IActions;
}

interface ISelect {
    id: number;
    name: string;
}

interface IAsyncSelect {
    value: number;
    label: string;
}

interface IState {
    hasSearched: boolean;
    isLoading: boolean;
    activePage: number;
    filter: IFilter;
}

interface IFilter {
    brokerAgentId: number;
    brokerAgentOptions?: { value: string; label: string }[];
    brokerFirmId: number;
    brokerFirmOptions?: { value: string; label: string }[];
    selectedBrokerFirm: IAsyncSelect;
    brokerChainId: number;
    regionId: number;
    sellerId: number;

    photoStatus: number;
    sketchUpStatus: number;
    roomSketcherStatus: number;

    orderNumber: string;
    createdStart: any;
    createdEnd: any;

    bookedStart: any;
    bookedEnd: any;

    address: string;
    zip: string;
    city: string;
    district: string;

    ownerFirstName: string;
    ownerLastName: string;

    invoiceStatus: number;
    photographerId: number;
    productId: number;
    productTypeId: number;
    invoiceReceiverType: number;

    contentProvider: number;
    bookingType: number;
    orderFilter?: number;

    editingStyle: number;
}

class AdvanceSearchPage extends React.Component<IProps, IState> {
    private formInputChangeHandler: FormInputChangeHandler;
    private defaultFilter: IFilter;

    constructor(props: IProps) {
        super(props);

        this.defaultFilter = {
            brokerAgentId: null,
            brokerAgentOptions: [],
            brokerFirmId: null,
            brokerFirmOptions: [],
            brokerChainId: null,
            regionId: null,
            sellerId: null,
            photoStatus: null,
            sketchUpStatus: null,
            roomSketcherStatus: null,
            orderNumber: '',
            createdStart: null,
            createdEnd: null,
            bookedStart: null,
            bookedEnd: null,
            address: '',
            zip: '',
            city: '',
            district: '',
            ownerFirstName: '',
            ownerLastName: '',
            invoiceStatus: null,
            photographerId: null,
            productId: null,
            productTypeId: null,
            invoiceReceiverType: null,
            bookingType: null,
            contentProvider: null,
            orderFilter: null,
            selectedBrokerFirm: null,
            editingStyle: null
        };

        this.state = {
            isLoading: false,
            activePage: 1,
            filter: assign({}, this.defaultFilter),
            hasSearched: false
        };

        this.getOrders = this.getOrders.bind(this);
        this.onPageChanged = this.onPageChanged.bind(this);
        this.search = this.search.bind(this);
        this.reset = this.reset.bind(this);
        this.onKeyPress = this.onKeyPress.bind(this);
        this.loadFirms = this.loadFirms.bind(this);
        this.loadAgents = this.loadAgents.bind(this);
        this.onBrokerChainChanged = this.onBrokerChainChanged.bind(this);
        this.onBrokerFirmChanged = this.onBrokerFirmChanged.bind(this);
        this.onBrokerAgentChanged = this.onBrokerAgentChanged.bind(this);
        this.formInputChangeHandler = new FormInputChangeHandler(
            this.state.filter,
            (newFilter: IFilter) => this.setState({ filter: newFilter } as IState)
        );
    }

    componentDidMount(): void {
        if (this.props.filter) {
            this.setState({ filter: assign({}, this.props.filter) } as IState);
            this.formInputChangeHandler.updateObj(this.props.filter);
            notify.info(Translate('orders/list/advance/toast/info/has-saved-filter'));
        }
        this.setState({ hasSearched: this.props.orders.length > 0 } as IState);
        this.getFilters();
    }

    getFilters() {
        if (!this.state.isLoading) {
            this.props.actions
                .getFilters()
                .then(() => {
                    this.setState({ isLoading: false } as IState);
                })
                .catch((error: any) => {
                    this.setState({ isLoading: false } as IState);
                    return handleError(error);
                });
        }
    }

    getOrders(page: number, filter?: IFilter) {
        if (!this.state.isLoading) {
            let currentFilter = filter ? filter : this.state.filter;
            this.setState({ isLoading: true } as IState);

            let params: any = assign({}, currentFilter);

            for (let key in params) {
                if (params[key] === -1) {
                    params[key] = null;
                }
            }

            params.page = page;

            if (currentFilter.createdStart) {
                const createdStart = moment(currentFilter.createdStart);
                if (createdStart instanceof moment) {
                    params.createdStartYear = moment(createdStart).year();
                    params.createdStartMonth = moment(createdStart).month() + 1;
                    params.createdStartDay = moment(createdStart).date();
                }
            }

            if (currentFilter.createdEnd) {
                const createdEnd = moment(currentFilter.createdEnd);
                if (createdEnd instanceof moment) {
                    params.createdEndYear = moment(createdEnd).year();
                    params.createdEndMonth = moment(createdEnd).month() + 1;
                    params.createdEndDay = moment(createdEnd).date();
                }
            }

            if (currentFilter.bookedStart) {
                const bookedStart = moment(currentFilter.bookedStart);
                if (bookedStart instanceof moment) {
                    params.bookedStartYear = moment(bookedStart).year();
                    params.bookedStartMonth = moment(bookedStart).month() + 1;
                    params.bookedStartDay = moment(bookedStart).date();
                }
            }

            if (currentFilter.bookedEnd) {
                const bookedEnd = moment(currentFilter.bookedEnd);
                if (bookedEnd instanceof moment) {
                    params.bookedEndYear = moment(bookedEnd).year();
                    params.bookedEndMonth =
                        moment(bookedEnd).month() + 1;
                    params.bookedEndDay = moment(bookedEnd).date();
                }
            }

            delete params.createdStart;
            delete params.createdEnd;
            delete params.bookedStart;
            delete params.bookedEnd;
            delete params.brokerAgentOptions;
            delete params.brokerFirmOptions;

            this.props.actions
                .getOrders(params)
                .then(() => {
                    this.setState({ isLoading: false } as IState);
                    if (page !== this.state.activePage) {
                        this.setState({ activePage: page } as IState);
                    }
                })
                .catch((error: any) => {
                    this.setState({ isLoading: false } as IState);
                    return handleError(error);
                });
        }
    }

    search() {
        this.props.actions.saveFilter(this.state.filter);
        this.setState({ hasSearched: true } as IState);
        this.getOrders(1);
    }

    onKeyPress(target) {
        if (target.charCode === 13) {
            this.search();
        }
    }

    reset() {
        let filter = assign({}, this.defaultFilter);
        this.formInputChangeHandler.updateObj(filter);
        this.props.actions.saveFilter(null);
        this.setState({ filter: filter } as IState);
    }

    onPageChanged(page: any) {
        this.getOrders(page);
    }

    onBrokerChainChanged(e: any) {
        let newFilter = assign({}, this.state.filter);
        newFilter.brokerChainId = e.target.value ? parseInt(e.target.value) : -1;
        this.setState({ filter: newFilter } as IState, () => {
            this.formInputChangeHandler.updateObj(this.state.filter);

            let chainId = this.state.filter.brokerChainId;
            let firmOptions = assign([], this.state.filter.brokerFirmOptions);
            let firm = firmOptions.find((x) => x.value === this.state.filter.brokerFirmId);

            if (firm) {
                if (firm.brokerChainId !== chainId) {
                    let newFilter = assign({}, this.state.filter);
                    newFilter.brokerFirmId = null;
                    newFilter.brokerAgentOptions = null;
                    newFilter.brokerAgentId = null;
                    let firmOptions = assign([], this.state.filter.brokerFirmOptions);
                    newFilter.brokerFirmOptions = firmOptions.filter(
                        (x) => x.brokerChainId === chainId
                    );
                    this.setState({ filter: newFilter } as IState, () => {
                        this.formInputChangeHandler.updateObj(this.state.filter);
                        this.loadAgents();
                    });
                }
            } else {
                let newFilter = assign({}, this.state.filter);
                newFilter.brokerFirmId = null;
                newFilter.brokerAgentOptions = null;
                newFilter.brokerAgentId = null;
                newFilter.brokerFirmOptions = [];
                this.setState({ filter: newFilter } as IState, () => {
                    this.formInputChangeHandler.updateObj(this.state.filter);
                    this.loadAgents();
                });
            }
        });
    }

    onBrokerFirmChanged(e: any) {
        let newFilter = assign({}, this.state.filter);
        newFilter.brokerFirmId = e ? e.value : null;
        newFilter.selectedBrokerFirm = e;

        this.setState({ filter: newFilter } as IState, () => {
            this.formInputChangeHandler.updateObj(this.state.filter);

            let agentOptions = assign([], this.state.filter.brokerAgentOptions);
            let agent = agentOptions.find((x) => x.value === this.state.filter.brokerAgentId);
            let firmId = this.state.filter.brokerFirmId;

            if (agent) {
                let newFilter = assign({}, this.state.filter);
                if (agent.brokerFirmId !== firmId) {
                    newFilter.brokerAgentId = null;
                }
                agentOptions = agentOptions.filter((x) => x.brokerFirmId === firmId);
                newFilter.brokerAgentOptions = agentOptions;
                this.setState({ filter: newFilter } as IState, () => {
                    this.formInputChangeHandler.updateObj(this.state.filter);
                    this.loadAgents();
                });
            } else {
                let newFilter = assign({}, this.state.filter);
                newFilter.brokerAgentId = null;
                newFilter.brokerAgentOptions = [];
                this.setState({ filter: newFilter } as IState, () => {
                    this.formInputChangeHandler.updateObj(this.state.filter);
                    this.loadAgents();
                });
            }
        });
    }

    onBrokerAgentChanged(e: any) {
        let newFilter = assign({}, this.state.filter);
        newFilter.brokerAgentId = e.target.value ? parseInt(e.target.value) : -1;
        this.setState({ filter: newFilter } as IState, () =>
            this.formInputChangeHandler.updateObj(this.state.filter)
        );
    }

    loadFirms(input, callback) {
        let chainId =
            this.state.filter.brokerChainId !== -1 ? this.state.filter.brokerChainId : null;

        if (!input) {
            return;
        }
        return actions.getFirmsForAdvanceSearch(input, chainId).then((result) => {
            this.formInputChangeHandler.handleChange('brokerFirmOptions', result);
            callback(result);
        });
    }

    loadAgents() {
        let chainId =
            this.state.filter.brokerChainId !== -1 ? this.state.filter.brokerChainId : null;
        let firmId = this.state.filter.brokerFirmId !== -1 ? this.state.filter.brokerFirmId : null;

        return actions.getAgentsForAdvanceSearch(chainId, firmId).then((result) => {
            let newFilter = assign({}, this.state.filter);
            newFilter.brokerAgentOptions = result;
            this.setState({ filter: newFilter } as IState, () =>
                this.formInputChangeHandler.updateObj(this.state.filter)
            );
        });
    }

    render() {
        return (
            <div>
                <h1>
                    {Translate('orders/list/advance/title')}{' '}
                    {this.state.hasSearched && !this.state.isLoading && (
                        <span className="number-of-orders">
                            ({this.props.totalNumberOfOrders ? this.props.totalNumberOfOrders : 0})
                        </span>
                    )}
                </h1>
                <div className="advanced-search-form">
                    <Row>
                        <Col md={9}>
                            <Row>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/broker-chain')}
                                        </ControlLabel>
                                        <FormSelect
                                            name="brokerChainId"
                                            value={
                                                this.state.filter.brokerChainId
                                                    ? this.state.filter.brokerChainId.toString()
                                                    : '-1'
                                            }
                                            onChange={this.onBrokerChainChanged}
                                        >
                                            <option value="-1">
                                                {Translate(
                                                    'orders/list/advance/filter/broker-chain/placeholder'
                                                )}
                                            </option>
                                            {this.props.availableBrokerChains &&
                                                this.props.availableBrokerChains.map((t: any) => (
                                                    <option key={t.id} value={t.id}>
                                                        {t.name}
                                                    </option>
                                                ))}
                                        </FormSelect>
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/broker-firm')}
                                        </ControlLabel>
                                        <Async
                                            isMulti={false}
                                            isClearable={true}
                                            placeholder={Translate(
                                                'orders/list/advance/filter/broker-firm/placeholder'
                                            )}
                                            noOptionsMessage={() =>
                                                Translate(
                                                    'orders/list/advance/filter/broker-firm/no-result'
                                                )
                                            }
                                            value={this.state.filter.selectedBrokerFirm}
                                            loadOptions={debounce(this.loadFirms, 350)}
                                            onChange={this.onBrokerFirmChanged}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/broker-agent')}
                                        </ControlLabel>
                                        <FormSelect
                                            disabled={
                                                !(
                                                    (this.state.filter.brokerChainId &&
                                                        this.state.filter.brokerChainId > 0) ||
                                                    (this.state.filter.brokerFirmId &&
                                                        this.state.filter.brokerFirmId > 0)
                                                )
                                            }
                                            name="brokerAgentId"
                                            value={
                                                this.state.filter.brokerAgentId
                                                    ? this.state.filter.brokerAgentId.toString()
                                                    : '-1'
                                            }
                                            onChange={this.onBrokerAgentChanged}
                                        >
                                            <option value="-1">
                                                {Translate(
                                                    'orders/list/advance/filter/broker-agent/placeholder'
                                                )}
                                            </option>
                                            {this.state.filter.brokerAgentOptions &&
                                                this.state.filter.brokerAgentOptions.map(
                                                    (t: any) => (
                                                        <option key={t.value} value={t.value}>
                                                            {t.label}
                                                        </option>
                                                    )
                                                )}
                                        </FormSelect>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/region')}
                                        </ControlLabel>
                                        <FormSelect
                                            name="regionId"
                                            value={
                                                this.state.filter.regionId
                                                    ? this.state.filter.regionId.toString()
                                                    : '-1'
                                            }
                                            onChange={this.formInputChangeHandler.onChange}
                                        >
                                            <option value="-1">
                                                {Translate(
                                                    'orders/list/advance/filter/region/placeholder'
                                                )}
                                            </option>
                                            {this.props.availableRegions &&
                                                this.props.availableRegions.map((t: any) => (
                                                    <option key={t.id} value={t.id}>
                                                        {t.name}
                                                    </option>
                                                ))}
                                        </FormSelect>
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/booking-type')}
                                        </ControlLabel>
                                        <FormSelect
                                            name="bookingType"
                                            value={
                                                this.state.filter.bookingType
                                                    ? this.state.filter.bookingType.toString()
                                                    : '-1'
                                            }
                                            onChange={this.formInputChangeHandler.onChange}
                                        >
                                            <option value="-1">
                                                {Translate(
                                                    'orders/list/advance/filter/booking-type/placeholder'
                                                )}
                                            </option>
                                            {this.props.availableBookingTypes &&
                                                this.props.availableBookingTypes.map((t: any) => (
                                                    <option key={t.id} value={t.id}>
                                                        {Translate(t.name)}
                                                    </option>
                                                ))}
                                        </FormSelect>
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/order-filter')}
                                        </ControlLabel>
                                        <FormSelect
                                            name="orderFilter"
                                            value={
                                                this.state.filter.orderFilter
                                                    ? this.state.filter.orderFilter.toString()
                                                    : ''
                                            }
                                            onChange={this.formInputChangeHandler.onChange}
                                        >
                                            <option value="-1">
                                                {Translate(
                                                    'orders/list/advance/filter/order-filter/placeholder'
                                                )}
                                            </option>
                                            {this.props.availableOrderFilters &&
                                                this.props.availableOrderFilters.map((t: any) => (
                                                    <option key={t.id} value={t.id}>
                                                        {Translate(t.name)}
                                                    </option>
                                                ))}
                                        </FormSelect>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate(
                                                'orders/list/advance/filter/sketchup-status'
                                            )}
                                        </ControlLabel>
                                        <FormSelect
                                            name="sketchUpStatus"
                                            value={
                                                this.state.filter.sketchUpStatus
                                                    ? this.state.filter.sketchUpStatus.toString()
                                                    : '-1'
                                            }
                                            onChange={this.formInputChangeHandler.onChange}
                                        >
                                            <option value="-1">
                                                {Translate(
                                                    'orders/list/advance/filter/sketchup-status/placeholder'
                                                )}
                                            </option>
                                            {this.props.availablePlanStatuses &&
                                                this.props.availablePlanStatuses.map((t: any) => (
                                                    <option key={t.id} value={t.id}>
                                                        {Translate(t.name)}
                                                    </option>
                                                ))}
                                        </FormSelect>
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate(
                                                'orders/list/advance/filter/roomsketcher-status'
                                            )}
                                        </ControlLabel>
                                        <FormSelect
                                            name="roomSketcherStatus"
                                            value={
                                                this.state.filter.roomSketcherStatus
                                                    ? this.state.filter.roomSketcherStatus.toString()
                                                    : '-1'
                                            }
                                            onChange={this.formInputChangeHandler.onChange}
                                        >
                                            <option value="-1">
                                                {Translate(
                                                    'orders/list/advance/filter/roomsketcher-status/placeholder'
                                                )}
                                            </option>
                                            {this.props.availablePlanStatuses &&
                                                this.props.availablePlanStatuses.map((t: any) => (
                                                    <option key={t.id} value={t.id}>
                                                        {Translate(t.name)}
                                                    </option>
                                                ))}
                                        </FormSelect>
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/photo-status')}
                                        </ControlLabel>
                                        <FormSelect
                                            name="photoStatus"
                                            value={
                                                this.state.filter.photoStatus
                                                    ? this.state.filter.photoStatus.toString()
                                                    : '-1'
                                            }
                                            onChange={this.formInputChangeHandler.onChange}
                                        >
                                            <option value="-1">
                                                {Translate(
                                                    'orders/list/advance/filter/photo-status/placeholder'
                                                )}
                                            </option>
                                            {this.props.availablePhotoStatuses &&
                                                this.props.availablePhotoStatuses.map((t: any) => (
                                                    <option key={t.id} value={t.id}>
                                                        {Translate(t.name)}
                                                    </option>
                                                ))}
                                        </FormSelect>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/ordernumber')}
                                        </ControlLabel>
                                        <FormControl
                                            type="text"
                                            name="orderNumber"
                                            value={this.state.filter.orderNumber}
                                            onKeyPress={this.onKeyPress}
                                            onChange={this.formInputChangeHandler.onChange}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate(
                                                'orders/list/advance/filter/created-date-start'
                                            )}
                                        </ControlLabel>
                                        <DateTime
                                            locale={moment.locale()}
                                            input={true}
                                            onChange={(value: any) =>
                                                this.formInputChangeHandler.handleChange(
                                                    'createdStart',
                                                    value
                                                )
                                            }
                                            timeFormat={false}
                                            value={this.state.filter.createdStart}
                                            isValidDate={(date) =>
                                                this.state.filter.createdEnd !== null
                                                    ? date.isSameOrBefore(
                                                          moment(this.state.filter.createdEnd)
                                                      )
                                                    : true
                                            }
                                            closeOnSelect={true}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate(
                                                'orders/list/advance/filter/created-date-end'
                                            )}
                                        </ControlLabel>
                                        <DateTime
                                            locale={moment.locale()}
                                            input={true}
                                            onChange={(value: any) =>
                                                this.formInputChangeHandler.handleChange(
                                                    'createdEnd',
                                                    value
                                                )
                                            }
                                            timeFormat={false}
                                            value={this.state.filter.createdEnd}
                                            isValidDate={(date) =>
                                                this.state.filter.createdStart !== null
                                                    ? date.isSameOrAfter(
                                                          moment(this.state.filter.createdStart)
                                                      )
                                                    : true
                                            }
                                            closeOnSelect={true}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate(
                                                'orders/list/advance/filter/booked-day-start'
                                            )}
                                        </ControlLabel>
                                        <DateTime
                                            locale={moment.locale()}
                                            input={true}
                                            onChange={(value: any) =>
                                                this.formInputChangeHandler.handleChange(
                                                    'bookedStart',
                                                    value
                                                )
                                            }
                                            timeFormat={false}
                                            value={this.state.filter.bookedStart}
                                            isValidDate={(date) =>
                                                this.state.filter.bookedEnd !== null
                                                    ? date.isSameOrBefore(
                                                          moment(this.state.filter.bookedEnd)
                                                      )
                                                    : true
                                            }
                                            closeOnSelect={true}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate(
                                                'orders/list/advance/filter/booked-day-end'
                                            )}
                                        </ControlLabel>
                                        <DateTime
                                            locale={moment.locale()}
                                            input={true}
                                            onChange={(value: any) =>
                                                this.formInputChangeHandler.handleChange(
                                                    'bookedEnd',
                                                    value
                                                )
                                            }
                                            timeFormat={false}
                                            value={this.state.filter.bookedEnd}
                                            isValidDate={(date) =>
                                                this.state.filter.bookedStart !== null
                                                    ? date.isSameOrAfter(
                                                          moment(this.state.filter.bookedStart)
                                                      )
                                                    : true
                                            }
                                            closeOnSelect={true}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/product-type')}
                                        </ControlLabel>
                                        <FormSelect
                                            name="productTypeId"
                                            value={
                                                this.state.filter.productTypeId
                                                    ? this.state.filter.productTypeId.toString()
                                                    : '-1'
                                            }
                                            onChange={this.formInputChangeHandler.onChange}
                                        >
                                            <option value="-1">
                                                {Translate(
                                                    'orders/list/advance/filter/product-type/placeholder'
                                                )}
                                            </option>
                                            {this.props.availableProductTypes &&
                                                this.props.availableProductTypes.map((t: ISelect) => (
                                                    <option key={t.id} value={t.id}>
                                                        {t.name}
                                                    </option>
                                                ))}
                                        </FormSelect>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate(
                                                'orders/list/advance/filter/owner-firstname'
                                            )}
                                        </ControlLabel>
                                        <FormControl
                                            type="text"
                                            name="ownerFirstName"
                                            value={this.state.filter.ownerFirstName}
                                            onKeyPress={this.onKeyPress}
                                            onChange={this.formInputChangeHandler.onChange}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/owner-lastname')}
                                        </ControlLabel>
                                        <FormControl
                                            type="text"
                                            name="ownerLastName"
                                            value={this.state.filter.ownerLastName}
                                            onKeyPress={this.onKeyPress}
                                            onChange={this.formInputChangeHandler.onChange}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/address')}
                                        </ControlLabel>
                                        <FormControl
                                            type="text"
                                            name="address"
                                            value={this.state.filter.address}
                                            onKeyPress={this.onKeyPress}
                                            onChange={this.formInputChangeHandler.onChange}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/zip')}
                                        </ControlLabel>
                                        <FormControl
                                            type="text"
                                            name="zip"
                                            value={this.state.filter.zip}
                                            onKeyPress={this.onKeyPress}
                                            onChange={this.formInputChangeHandler.onChange}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/city')}
                                        </ControlLabel>
                                        <FormControl
                                            type="text"
                                            name="city"
                                            value={this.state.filter.city}
                                            onKeyPress={this.onKeyPress}
                                            onChange={this.formInputChangeHandler.onChange}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/district')}
                                        </ControlLabel>
                                        <FormControl
                                            type="text"
                                            name="district"
                                            value={this.state.filter.district}
                                            onKeyPress={this.onKeyPress}
                                            onChange={this.formInputChangeHandler.onChange}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    <FormGroup>
                                        <ControlLabel>
                                            {Translate('orders/list/advance/filter/editing-style')}
                                        </ControlLabel>
                                        <FormSelect
                                            name="editingStyle"
                                            value={
                                                this.state.filter.editingStyle
                                                    ? this.state.filter.editingStyle.toString()
                                                    : '-1'
                                            }
                                            onChange={this.formInputChangeHandler.onChange}
                                        >
                                            <option value="-1">
                                                {Translate(
                                                    'orders/list/advance/filter/editing-style/placeholder'
                                                )}
                                            </option>
                                            {this.props.availableEditStyles &&
                                                this.props.availableEditStyles.map((t: any) => (
                                                    <option key={t.id} value={t.id}>
                                                        {t.name}
                                                    </option>
                                                ))}
                                        </FormSelect>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <ControlLabel>
                                    {Translate('orders/list/advance/filter/invoice-status')}
                                </ControlLabel>
                                <FormSelect
                                    name="invoiceStatus"
                                    value={
                                        this.state.filter.invoiceStatus
                                            ? this.state.filter.invoiceStatus.toString()
                                            : '-1'
                                    }
                                    onChange={this.formInputChangeHandler.onChange}
                                >
                                    <option value="-1">
                                        {Translate(
                                            'orders/list/advance/filter/invoice-status/placeholder'
                                        )}
                                    </option>
                                    {this.props.availableInvoiceStatuses &&
                                        this.props.availableInvoiceStatuses.map((t: any) => (
                                            <option key={t.id} value={t.id}>
                                                {Translate(t.name)}
                                            </option>
                                        ))}
                                </FormSelect>
                            </FormGroup>
                            <FormGroup>
                                <ControlLabel>
                                    {Translate('orders/list/advance/filter/photographer')}
                                </ControlLabel>
                                <FormSelect
                                    name="photographerId"
                                    value={
                                        this.state.filter.photographerId
                                            ? this.state.filter.photographerId.toString()
                                            : '-1'
                                    }
                                    onChange={this.formInputChangeHandler.onChange}
                                >
                                    <option value="-1">
                                        {Translate(
                                            'orders/list/advance/filter/photographer/placeholder'
                                        )}
                                    </option>
                                    {this.props.availablePhotographers &&
                                        this.props.availablePhotographers.map((t: any) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ))}
                                </FormSelect>
                            </FormGroup>
                            <FormGroup>
                                <ControlLabel>
                                    {Translate('orders/list/advance/filter/product')}
                                </ControlLabel>
                                <FormSelect
                                    name="productId"
                                    value={
                                        this.state.filter.productId
                                            ? this.state.filter.productId.toString()
                                            : '-1'
                                    }
                                    onChange={this.formInputChangeHandler.onChange}
                                >
                                    <option value="-1">
                                        {Translate(
                                            'orders/list/advance/filter/product/placeholder'
                                        )}
                                    </option>
                                    {this.props.availableProducts &&
                                        this.props.availableProducts.map((t: any) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ))}
                                </FormSelect>
                            </FormGroup>
                            <FormGroup>
                                <ControlLabel>
                                    {Translate('orders/list/advance/filter/invoice-receiver-type')}
                                </ControlLabel>
                                <FormSelect
                                    name="invoiceReceiverType"
                                    value={
                                        this.state.filter.invoiceReceiverType
                                            ? this.state.filter.invoiceReceiverType.toString()
                                            : '-1'
                                    }
                                    onChange={this.formInputChangeHandler.onChange}
                                >
                                    <option value="-1">
                                        {Translate(
                                            'orders/list/advance/filter/invoice-receiver-type/placeholder'
                                        )}
                                    </option>
                                    {this.props.availableInvoiceReceiverTypes &&
                                        this.props.availableInvoiceReceiverTypes.map((t: any) => (
                                            <option key={t.id} value={t.id}>
                                                {Translate(t.name)}
                                            </option>
                                        ))}
                                </FormSelect>
                            </FormGroup>
                            <FormGroup>
                                <ControlLabel>
                                    {Translate('orders/list/advance/filter/content-provider')}
                                </ControlLabel>
                                <FormSelect
                                    name="contentProvider"
                                    value={
                                        this.state.filter.contentProvider
                                            ? this.state.filter.contentProvider.toString()
                                            : '-1'
                                    }
                                    onChange={this.formInputChangeHandler.onChange}
                                >
                                    <option value="-1">
                                        {Translate(
                                            'orders/list/advance/filter/content-provider/placeholder'
                                        )}
                                    </option>
                                    {this.props.availableContentProviders &&
                                        this.props.availableContentProviders.map((t: any) => (
                                            <option key={t.id} value={t.id}>
                                                {Translate(t.name)}
                                            </option>
                                        ))}
                                </FormSelect>
                            </FormGroup>
                            <FormGroup>
                                <ControlLabel>
                                    {Translate('orders/list/advance/filter/seller')}
                                </ControlLabel>
                                <FormSelect
                                    name="sellerId"
                                    value={
                                        this.state.filter.sellerId
                                            ? this.state.filter.sellerId.toString()
                                            : '-1'
                                    }
                                    onChange={this.formInputChangeHandler.onChange}
                                >
                                    <option value="-1">
                                        {Translate('orders/list/advance/filter/seller/placeholder')}
                                    </option>
                                    {this.props.availableSellers &&
                                        this.props.availableSellers.map((t: any) => (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ))}
                                </FormSelect>
                            </FormGroup>
                        </Col>
                    </Row>
                    <ButtonToolbar>
                        <ButtonGroup>
                            <Button data-testid="advance-search-btn" onClick={this.search} bsStyle="primary">
                                <i className="material-icons md-18">search</i>{' '}
                                {Translate('orders/list/advance/filter/search-button')}
                            </Button>
                        </ButtonGroup>
                        <ButtonGroup>
                            <Button data-testid="advance-reset-btn" onClick={this.reset}>
                                {Translate('orders/list/advance/filter/reset-button')}
                            </Button>
                        </ButtonGroup>
                    </ButtonToolbar>
                </div>
                <hr />
                {this.props.orders && this.props.orders.length > 0 && (
                    <List items={this.props.orders} user={this.props.user} />
                )}
                {this.props.orders && this.props.orders.length === 0 && (
                    <div data-testid="advance-no-results">{Translate('orders/list/advance/no-result')}</div>
                )}
                {this.props.orders && this.props.orders.length > 0 && (
                    <Pagination
                        numberOfPages={this.props.numberOfPages}
                        activePage={this.state.activePage}
                        maxNumberOfButtons={5}
                        onPageSelect={this.onPageChanged}
                    />
                )}
                <Loader loaded={!this.state.isLoading} scale={2.0} />
            </div>
        );
    }
}

function mapStateToProps(state: any, ownProps: any) {
    return {
        user: state.user,
        orders: state.orders.advanceList.result.orders,
        availablePhotoStatuses: state.orders.advanceList.filters.availablePhotoStatuses,
        availablePlanStatuses: state.orders.advanceList.filters.availablePlanStatuses,
        availableInvoiceStatuses: state.orders.advanceList.filters.availableInvoiceStatuses,
        availableBrokerChains: state.orders.advanceList.filters.availableBrokerChains,
        availableRegions: state.orders.advanceList.filters.availableRegions,
        availablePhotographers: state.orders.advanceList.filters.availablePhotographers,
        availableProducts: state.orders.advanceList.filters.availableProducts,
        availableProductTypes: state.orders.advanceList.filters.availableProductTypes,
        availableInvoiceReceiverTypes:
            state.orders.advanceList.filters.availableInvoiceReceiverTypes,
        availableContentProviders: state.orders.advanceList.filters.availableContentProviders,
        availableBookingTypes: state.orders.advanceList.filters.availableBookingTypes,
        availableSellers: state.orders.advanceList.filters.availableSellers,
        availableOrderFilters: state.orders.advanceList.filters.availableOrderFilters,
        availableEditStyles: state.orders.advanceList.filters.availableEditStyles,
        totalNumberOfOrders: state.orders.advanceList.result.totalNumberOfOrders,
        numberOfPages: state.orders.advanceList.result.numberOfPages,
        filter: state.orders.filters.advanceListFilter
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            getOrders: (params: any) => dispatch(actions.getAdvanceSearchOrders(params)),
            getFirms: (name: string, brokerChainId: number) =>
                dispatch(actions.getFirmsForAdvanceSearch(name, brokerChainId)),
            getAgents: (brokerChainId: number, brokerFirmId: number) =>
                dispatch(actions.getAgentsForAdvanceSearch(brokerChainId, brokerFirmId)),
            saveFilter: (filter: IFilter) => dispatch(actions.saveFilter(filter)),
            getFilters: () => dispatch(actions.getAdvanceSearchFilters())
        }
    };
}

interface IActions {
    getOrders: (params: any) => Promise<any>;
    getFirms: (name: string, brokerChainId: number) => Promise<any>;
    getAgents: (brokerChainId: number, brokerFirmId: number) => Promise<any>;
    saveFilter: (filter: IFilter) => Promise<any>;
    getFilters: () => Promise<any>;
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(AdvanceSearchPage));
